import clsx from 'clsx';
import { ChevronRightIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { formatDateSafe } from '@/utils/dateUtils';
import { getModalityLabel } from '../../../vacations.definitions';
import { vacationReceiptService } from '../../../services/vacationReceiptService';
import { useDraftCountdown } from '../../../hooks/useDraftCountdown';
import { ConfirmRequestAction } from '../../VacationRequestDetailsModal/components';
import { RejectionNotice } from './RejectionNotice';
import { SellDecisionNotice } from './SellDecisionNotice';
import {
    SELL_STATUS_LABELS,
    getRequestStatusMetadata,
    getReceiptPendingLabel,
    VIEW_DETAILS_LABEL,
    getViewDetailsAriaLabel,
    buildDraftCountdownLabel,
} from '../vacationRequestList.definitions';
import type { VacationRequestCardProps } from '../vacationRequestList.types';

export function VacationRequestCard({
    request,
    onSelect,
    onConfirmRequest,
    isConfirmingRequest = false,
    receipts,
}: VacationRequestCardProps) {
    const { remainingLabel, isExpired } = useDraftCountdown(request);
    const status = getRequestStatusMetadata(request);
    const receiptPendingLabel = getReceiptPendingLabel(request);
    const showDraftCountdown = request.status === 'Draft' && !isExpired;
    const sellRejectedByDp =
        request.sellRequest.status === 'Rejected' && request.status !== 'Rejected';
    const sellLabel = sellRejectedByDp ? '' : SELL_STATUS_LABELS[request.sellRequest.status] ?? '';
    const modalityLabel = getModalityLabel(request.modality);
    const canConfirm = Boolean(onConfirmRequest) && request.status === 'Draft';

    return (
        <li className="relative rounded-xl border border-primary-200 bg-white p-4 shadow-sm transition-shadow hover:border-primary-400 hover:shadow-md focus-within:ring-2 focus-within:ring-primary-500">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <p className="text-sm font-semibold text-raro-gray-900 font-montserrat">
                        {modalityLabel} · {request.totalDays} dias de gozo
                    </p>
                    <p className="mt-1 text-xs text-raro-gray-500 font-montserrat">
                        Solicitada em {formatDateSafe(request.requestedAt?.toDate?.())}
                    </p>
                    {showDraftCountdown && (
                        <p className="mt-1 text-xs text-raro-gray-600 font-montserrat">
                            {buildDraftCountdownLabel(remainingLabel)}
                        </p>
                    )}
                </div>

                <div className="flex shrink-0 items-center gap-1">
                    <span
                        className={clsx(
                            'inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold font-montserrat',
                            status.className
                        )}
                    >
                        {status.label}
                    </span>
                    {receiptPendingLabel && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800 font-montserrat">
                            {receiptPendingLabel}
                        </span>
                    )}
                    <ChevronRightIcon className="h-4 w-4 text-raro-gray-400" aria-hidden="true" />
                </div>
            </div>

            <ul className="mt-3 space-y-1">
                {request.segments.map((segment) => (
                    <li key={segment.order} className="text-sm text-raro-gray-700 font-montserrat">
                        {formatDateSafe(segment.startDate)} a {formatDateSafe(segment.endDate)}{' '}
                        <span className="text-raro-gray-500">({segment.days} dias)</span>
                    </li>
                ))}
            </ul>

            {sellLabel && (
                <p className="mt-2 text-xs text-primary-700 font-montserrat">
                    {sellLabel} — {request.sellRequest.days} dias
                </p>
            )}

            <div className="mt-2 flex items-center justify-between">
                <p className="text-xs font-semibold text-primary-700 font-montserrat">
                    {VIEW_DETAILS_LABEL}
                </p>
                <div className="flex items-center gap-3">
                    {receipts && receipts.length > 0 && (
                        <div className="flex items-center gap-2">
                            {receipts.map((receipt) => (
                                <button
                                    key={receipt.id}
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        void vacationReceiptService.openFile(receipt);
                                    }}
                                    className="relative z-10 inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:text-primary-800 font-montserrat"
                                    title={
                                        receipt.segmentOrder !== null && receipt.segmentOrder !== undefined
                                            ? `Recibo do ${receipt.segmentOrder}º período`
                                            : 'Recibo'
                                    }
                                >
                                    <ArrowDownTrayIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                    {receipts.length > 1 && receipt.segmentOrder !== null
                                        ? `Recibo ${receipt.segmentOrder}º`
                                        : 'Baixar recibo'}
                                </button>
                            ))}
                        </div>
                    )}
                    {canConfirm && (
                        <div className="relative z-10">
                            <ConfirmRequestAction
                                onConfirm={() => onConfirmRequest?.(request.id)}
                                isConfirming={isConfirmingRequest}
                                compact
                            />
                        </div>
                    )}
                </div>
            </div>

            {sellRejectedByDp && (
                <SellDecisionNotice note={request.sellRequest.note} />
            )}

            {request.status === 'Rejected' && (
                <RejectionNotice note={request.managerApproval.note} />
            )}

            <button
                type="button"
                onClick={onSelect}
                aria-label={getViewDetailsAriaLabel(modalityLabel)}
                className="absolute inset-0 rounded-xl focus:outline-none"
            />
        </li>
    );
}

export default VacationRequestCard;
